import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that manages a non-modal cart drawer.
 * Mirrors the product-info-drawer architecture: fixed right panel + overlay portalled
 * to <body>, opened/closed via CSS classes. Page scroll is preserved while drawer is open.
 */
class CartDrawerComponent extends HTMLElement {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {boolean} */
  #open = false;

  /** @type {HTMLElement | null} */
  #panel = null;

  /** @type {HTMLElement | null} */
  #overlay = null;

  /** @type {HTMLElement | null} */
  #trigger = null;

  connectedCallback() {
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Morph can move this element (disconnect + reconnect): the panel is already portalled, don't set up twice.
    if (this.#panel) return;

    this.#panel = this.querySelector('[ref="panel"]');
    this.#overlay = this.querySelector('.cart-drawer__overlay');
    this.#trigger = this.querySelector('[data-testid="cart-drawer-trigger"]');

    this.#trigger?.addEventListener('click', () => this.open());

    // Portal panel and overlay to <body> so they break out of any ancestor stacking context.
    if (this.#panel) document.body.appendChild(this.#panel);
    if (this.#overlay) document.body.appendChild(this.#overlay);

    // Prevent section-renderer/morph from re-adding portalled children on cart re-renders.
    this.setAttribute('data-skip-subtree-update', '');

    this.#overlay?.addEventListener('click', () => this.close());

    // Close buttons inside the portalled panel won't bubble up to cart-drawer-component,
    // so we delegate close actions on the panel directly.
    this.#panel?.addEventListener('click', (e) => {
      if (/** @type {Element} */ (e.target).closest('[ref="closeButton"]')) {
        this.close();
      }
    }, { capture: true });

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    // Only drop the portalled nodes if the element was really removed, not just moved.
    queueMicrotask(() => {
      if (this.isConnected) return;
      this.#panel?.remove();
      this.#overlay?.remove();
    });
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener('keydown', this.#handleKeyDown);
    this.#historyAbortController?.abort();
  }

  #isOpen() {
    return this.#open;
  }

  #handleKeyDown = (/** @type {KeyboardEvent} */ e) => {
    if (e.key === 'Escape' && this.#isOpen()) {
      e.preventDefault();
      this.close();
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  #handleCartUpdate = (event) => {
    const sections = event.detail?.data?.sections;
    if (!sections || !this.#panel) return;

    const cartItemsComponent = this.#panel.querySelector('cart-items-component');
    if (!(cartItemsComponent instanceof HTMLElement)) return;

    const sectionId = cartItemsComponent.dataset.sectionId;
    if (!sectionId) return;

    const sectionHTML = sections[sectionId];
    if (!sectionHTML) return;

    const newDoc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newPanel = newDoc.querySelector('[ref="panel"]');
    if (!newPanel) return;

    // Sync the --empty modifier class (it lives on the panel element itself,
    // so childrenOnly morph below won't touch it).
    this.#panel.classList.toggle(
      'cart-drawer__panel--empty',
      newPanel.classList.contains('cart-drawer__panel--empty')
    );

    // Morph panel children only (MORPH_OPTIONS.childrenOnly = true) so runtime
    // attributes on the panel itself (e.g. the `is-open` class) are preserved.
    morph(this.#panel, newPanel, MORPH_OPTIONS);

    this.#updateStickyState();
  };

  #handleHistoryOpen() {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  }

  #handleHistoryClose() {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  }

  #handlePopState = () => {
    if (this.#isOpen()) {
      this.#closeInternal({ skipHistory: true });
    }
  };

  open() {
    if (this.#open) return;

    this.#open = true;
    this.setAttribute('open', '');
    this.#panel?.classList.add('is-open');
    this.#overlay?.classList.add('is-open');

    document.addEventListener('keydown', this.#handleKeyDown);

    this.#handleHistoryOpen();
    this.#updateStickyState();

    // Focus first interactive element inside the panel
    requestAnimationFrame(() => {
      const focusable = this.#panel?.querySelector(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    });

    // Close cart drawer when installments CTA is clicked to avoid overlapping panels
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.close(), { once: true });
    });
  }

  #closeInternal({ skipHistory = false } = {}) {
    if (!this.#open) return;

    this.#open = false;
    this.removeAttribute('open');
    this.#panel?.classList.remove('is-open');
    this.#overlay?.classList.remove('is-open');

    document.removeEventListener('keydown', this.#handleKeyDown);

    if (!skipHistory) {
      this.#handleHistoryClose();
    } else {
      this.#historyAbortController?.abort();
    }

    this.#trigger?.focus();
  }

  close() {
    this.#closeInternal();
  }

  #updateStickyState() {
    const panel = this.#panel;
    if (!panel) return;

    const content = panel.querySelector('.cart-drawer__content');
    const summary = panel.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      panel.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const panelHeight = panel.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / panelHeight;
    panel.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
